import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import QRCode from 'qrcode'

import {
  BaseCard,
  Button,
  InputRow,
  PendingContent,
} from '@lattice/common/components'
import {
  useFetchableOperation,
  useFetchableResource,
  useProgressToasts,
} from '@lattice/common/hooks'
import { useUserProvider } from '@lattice/common/providers'
import { FetchStatus } from '@lattice/common/consts'

import styles from './component.module.scss'

type IEnableMFormData = {
  mfaCode: string
}

const MFAEnableCard = ({ onCancelClick }: { onCancelClick?: () => void }) => {
  const { t } = useTranslation()
  const {
    user,
    userAttributes,
    doGenerateMfaTotpSecret,
    doVerifyMfaTotpToken,
    doSetMfaPreference,
    doFetchUserMfaPreference,
  } = useUserProvider()
  const enableMfaOperation = useFetchableOperation()
  const enableMfaOperationProgressToasts = useProgressToasts()

  const totpSeedContentUrl = useFetchableResource<string | null>(null)
  const userEnableMfaForm = useForm<IEnableMFormData>({
    mode: 'onTouched',
    defaultValues: { mfaCode: '' },
  })

  const fetchTotpSeed = totpSeedContentUrl.wrappedFetch(async () => {
    if (!userAttributes.resource) {
      throw new Error('Invalid user attributes state')
    }
    const totpSeed = await doGenerateMfaTotpSecret()

    console.log('totpSeed', totpSeed)

    const totpURIParams = new URLSearchParams()
    totpURIParams.set('secret', totpSeed)
    totpURIParams.set('issuer', 'Lattice Gateway')

    const totpURI = `otpauth://totp/${window.encodeURIComponent(
      userAttributes.resource?.find((attr) => attr.Name === 'email')?.Value ??
        '--'
    )}?${totpURIParams}`

    const svgContent = await QRCode.toString(totpURI, {
      type: 'svg',
      errorCorrectionLevel: 'low',
      margin: 0,
      color: {
        dark: '#000000ff',
        light: '#ffffffff',
      },
    })

    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' })

    return URL.createObjectURL(svgBlob)
  })

  const doEnableMfa = userEnableMfaForm.handleSubmit(
    enableMfaOperation.wrappedFetch(
      enableMfaOperationProgressToasts.wrappedErrorsAsync(async (data) => {
        await doVerifyMfaTotpToken(data.mfaCode)

        await doSetMfaPreference('SOFTWARE_TOKEN_MFA')

        await doFetchUserMfaPreference()

        enableMfaOperationProgressToasts.progress(
          '2FA Authentication Enabled',
          'success',
          15000
        )

        onCancelClick && onCancelClick()
      })
    )
  )

  useEffect(() => {
    fetchTotpSeed()
  }, [user])

  return (
    <BaseCard
      variants={['header-title']}
      header={t(
        'views.User.views.MyAccount.components.MFAEnableCard.twoFactor',
        'Two-factor Authentication'
      )}
      className={{ root: styles.root, body: styles.body }}
    >
      <form onSubmit={doEnableMfa}>
        <span>
          {t(
            'views.User.views.MyAccount.components.MFAEnableCard.scanThisQR',
            'Scan this QR with your mobile authentication app to receive a 6-digit, one-time use code.'
          )}
        </span>
        {totpSeedContentUrl.status === FetchStatus.PENDING && (
          <PendingContent />
        )}
        {totpSeedContentUrl.status === FetchStatus.DONE &&
          totpSeedContentUrl.resource && (
            <img src={totpSeedContentUrl.resource} />
          )}
        <Controller
          name="mfaCode"
          rules={{
            required: true,
            validate: (value) =>
              value.length === 6 ||
              String(
                t(
                  'views.User.views.SignIn.mfaCodeError',
                  'Code is a 6 digit number'
                )
              ),
          }}
          control={userEnableMfaForm.control}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          render={({ field: { ref, ...rest }, fieldState }) => (
            <InputRow.Numeric
              variants={['full-width']}
              placeholder={t(
                'views.User.views.MyAccount.components.MFAEnableCard.enterYourVerifiaction',
                'Enter your verification code'
              )}
              error={fieldState.error?.message}
              valueType="value"
              format="### ###"
              {...rest}
            />
          )}
        />
        <Button
          variants={['full-width', 'primary']}
          type="submit"
          disabled={
            !userEnableMfaForm.formState.isValid ||
            enableMfaOperation.status === FetchStatus.PENDING
          }
          loading={enableMfaOperation.status === FetchStatus.PENDING}
        >
          {t(
            'views.User.views.MyAccount.components.MFAEnableCard.enable',
            'Enable'
          )}
        </Button>
        <Button
          variants={['full-width', 'secondary']}
          type="button"
          disabled={enableMfaOperation.status === FetchStatus.PENDING}
          onClick={
            enableMfaOperation.status === FetchStatus.PENDING
              ? undefined
              : onCancelClick
          }
        >
          {t(
            'views.User.views.MyAccount.components.MFAEnableCard.cancel',
            'Cancel'
          )}
        </Button>
      </form>
    </BaseCard>
  )
}

export { MFAEnableCard }
